/**
 * Homepage Copy Editor
 * /admin/homepage-copy
 *
 * Compact WYSIWYG-ish editor for the homepage hero copy:
 *   • Trust badge
 *   • Title (main + highlight)
 *   • Sub-headline
 *   • Primary CTA (label + link)
 *   • Hero stat cards (up to 3)
 *
 * Writes to the same page_content record that HomePageRedesigned already
 * reads on mount (`GET /api/cms/pages/public/homepage`), so changes go live
 * on the next page load.
 */
import { useState, useEffect } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sparkles,
  Save,
  Loader2,
  Plus,
  Trash2,
  ExternalLink,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const ICON_OPTIONS = [
  { value: 'GraduationCap', label: 'Graduation Cap' },
  { value: 'Globe', label: 'Globe' },
  { value: 'Trophy', label: 'Trophy' },
  { value: 'Users', label: 'Users' },
  { value: 'Award', label: 'Award' },
  { value: 'Star', label: 'Star' },
  { value: 'BookOpen', label: 'Book' },
];

const DEFAULT_HERO = {
  badge_text: 'Trusted by 30,000+ Students Worldwide',
  title: 'DREAM BIG.',
  title_highlight: 'GO FAR.',
  subtitle:
    "Your journey to a world-class education starts here. We've helped thousands discover their dream universities and make it a reality.",
  cta_primary_text: 'Find My Dream University',
  cta_primary_link: '/uac-desk/register',
};

const DEFAULT_STAT_CARDS = [
  { icon: 'GraduationCap', value: '500+ Partner Universities', label: '', sublabel: 'Across 15 countries, waiting for you', featured: true },
  { icon: 'Globe', value: '15', label: 'Countries', sublabel: '', featured: false },
  { icon: 'Trophy', value: '98%', label: 'Visa Success', sublabel: '', featured: false },
];

export const HomepageCopyEditor = () => {
  const { token } = useCMSAuth();
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [statCards, setStatCards] = useState(DEFAULT_STAT_CARDS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState({ hero: DEFAULT_HERO, statCards: DEFAULT_STAT_CARDS });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/pages/public/homepage`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        const c = data.content || {};
        const currentHero = { ...DEFAULT_HERO, ...(c.hero || {}) };
        const currentCards = Array.isArray(c.hero_stat_cards) && c.hero_stat_cards.length > 0
          ? c.hero_stat_cards.slice(0, 3)
          : DEFAULT_STAT_CARDS;
        setHero(currentHero);
        setStatCards(currentCards);
        setInitial({ hero: currentHero, statCards: currentCards });
      } catch (e) {
        toast.error('Could not load current homepage content.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setStat = (idx, field, val) => {
    setStatCards((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: val } : s)));
  };

  const removeStat = (idx) => {
    setStatCards((prev) => prev.filter((_, i) => i !== idx));
  };

  const addStat = () => {
    setStatCards((prev) => [...prev, { icon: 'Star', value: '', label: '', sublabel: '', featured: false }]);
  };

  const resetToDefaults = () => {
    setHero(DEFAULT_HERO);
    setStatCards(DEFAULT_STAT_CARDS);
    toast.info('Reset to defaults. Click Save to publish.');
  };

  const save = async () => {
    setSaving(true);
    try {
      // First fetch the existing content so we don't overwrite other sections
      const existRes = await fetch(`${API_URL}/api/cms/pages/public/homepage`);
      const existData = existRes.ok ? await existRes.json() : { content: {} };
      const nextContent = {
        ...(existData.content || {}),
        hero,
        hero_stat_cards: statCards
          .map((s) => ({
            icon: (s.icon || '').trim() || 'Star',
            value: (s.value || '').trim(),
            label: (s.label || '').trim(),
            sublabel: (s.sublabel || '').trim(),
            featured: !!s.featured,
          }))
          .filter((s) => s.value || s.label),
      };
      const res = await fetch(`${API_URL}/api/cms/pages/homepage`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content: nextContent }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setInitial({ hero, statCards });
      toast.success('Homepage copy updated. Refresh the homepage to see it live.');
    } catch (e) {
      toast.error('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    JSON.stringify(hero) !== JSON.stringify(initial.hero) ||
    JSON.stringify(statCards) !== JSON.stringify(initial.statCards);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="homepage-copy-editor">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-ux-cyan" /> Homepage Hero
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Edit the top-of-fold copy: trust badge, headline, sub-headline, primary CTA, and the three
              glass stat cards. Saves publish immediately on next page load.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <a href="/" target="_blank" rel="noreferrer">
                <ExternalLink className="w-4 h-4 mr-1.5" /> Preview homepage
              </a>
            </Button>
            <Button variant="ghost" onClick={resetToDefaults} data-testid="homepage-copy-reset">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reset to defaults
            </Button>
            <Button onClick={save} disabled={!hasChanges || saving} data-testid="homepage-copy-save">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1.5" /> Save changes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Live preview */}
        <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-white/10 overflow-hidden">
          <CardContent className="p-8">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-4">Preview</div>
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-white/10 mb-4">
                <Sparkles className="w-3.5 h-3.5 text-ux-cyan" />
                {hero.badge_text}
              </div>
              <div className="font-display text-3xl md:text-4xl font-bold leading-tight">
                {hero.title}
                <br />
                <span className="bg-gradient-to-r from-ux-cyan to-ux-blue bg-clip-text text-transparent">
                  {hero.title_highlight}
                </span>
              </div>
              <p className="text-sm text-white/70 mt-3">{hero.subtitle}</p>
              <div className="mt-4 inline-block px-4 py-2 rounded-full bg-gradient-to-r from-ux-cyan to-ux-blue text-white text-sm font-medium">
                {hero.cta_primary_text}
              </div>
            </div>
            {statCards.length > 0 && (
              <div className="grid sm:grid-cols-3 gap-3 mt-6 max-w-2xl">
                {statCards.map((s, i) => (
                  <div key={i} className={`p-3 rounded-lg bg-white/5 border ${s.featured ? 'border-ux-cyan/40' : 'border-white/10'}`}>
                    <div className="text-lg font-bold">{s.value}</div>
                    <div className="text-xs text-white/70">{s.label}</div>
                    {s.sublabel && <div className="text-[10px] text-white/40 mt-1">{s.sublabel}</div>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Hero copy panel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Hero copy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Trust badge</Label>
                <Input
                  value={hero.badge_text || ''}
                  onChange={(e) => setHero((h) => ({ ...h, badge_text: e.target.value }))}
                  data-testid="homepage-badge"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Headline (main)</Label>
                  <Input
                    value={hero.title || ''}
                    onChange={(e) => setHero((h) => ({ ...h, title: e.target.value }))}
                    data-testid="homepage-title"
                  />
                </div>
                <div>
                  <Label className="text-xs">Headline (highlight)</Label>
                  <Input
                    value={hero.title_highlight || ''}
                    onChange={(e) => setHero((h) => ({ ...h, title_highlight: e.target.value }))}
                    data-testid="homepage-title-highlight"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Sub-headline</Label>
                <Textarea
                  rows={3}
                  value={hero.subtitle || ''}
                  onChange={(e) => setHero((h) => ({ ...h, subtitle: e.target.value }))}
                  data-testid="homepage-subtitle"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">CTA label</Label>
                  <Input
                    value={hero.cta_primary_text || ''}
                    onChange={(e) => setHero((h) => ({ ...h, cta_primary_text: e.target.value }))}
                    data-testid="homepage-cta-text"
                  />
                </div>
                <div>
                  <Label className="text-xs">CTA link</Label>
                  <Input
                    value={hero.cta_primary_link || ''}
                    onChange={(e) => setHero((h) => ({ ...h, cta_primary_link: e.target.value }))}
                    placeholder="/uac-desk/register"
                    data-testid="homepage-cta-link"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stat cards panel */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Hero stat cards ({statCards.length})</CardTitle>
              {statCards.length < 3 && (
                <Button size="sm" variant="outline" onClick={addStat}>
                  <Plus className="w-4 h-4 mr-1" /> Add card
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {statCards.length === 0 && (
                <p className="text-xs text-slate-400 italic">No stat cards. Add one to feature key metrics beside the hero.</p>
              )}
              {statCards.map((s, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-2" data-testid={`homepage-stat-card-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">Card {i + 1}</span>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!s.featured}
                          onChange={(e) => setStat(i, 'featured', e.target.checked)}
                          data-testid={`homepage-stat-featured-${i}`}
                        />
                        Featured
                      </label>
                      <button type="button" onClick={() => removeStat(i)} className="text-red-500 hover:text-red-700" data-testid={`homepage-stat-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px]">Icon</Label>
                      <select
                        value={s.icon || ''}
                        onChange={(e) => setStat(i, 'icon', e.target.value)}
                        className="w-full h-8 text-xs border rounded px-2 bg-white"
                        data-testid={`homepage-stat-icon-${i}`}
                      >
                        {ICON_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-[10px]">Value</Label>
                      <Input
                        className="h-8 text-xs"
                        value={s.value || ''}
                        onChange={(e) => setStat(i, 'value', e.target.value)}
                        placeholder="e.g. 500+ Partner Universities"
                        data-testid={`homepage-stat-value-${i}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px]">Label</Label>
                      <Input
                        className="h-8 text-xs"
                        value={s.label || ''}
                        onChange={(e) => setStat(i, 'label', e.target.value)}
                        placeholder="e.g. Countries"
                        data-testid={`homepage-stat-label-${i}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px]">Sub-label</Label>
                      <Input
                        className="h-8 text-xs"
                        value={s.sublabel || ''}
                        onChange={(e) => setStat(i, 'sublabel', e.target.value)}
                        placeholder="optional caption"
                        data-testid={`homepage-stat-sublabel-${i}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {hasChanges && (
          <div className="fixed bottom-6 right-6 bg-white border border-ux-cyan shadow-xl rounded-full pl-4 pr-2 py-1.5 flex items-center gap-3 z-40" data-testid="homepage-copy-unsaved">
            <span className="text-xs text-slate-600">Unsaved changes</span>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default HomepageCopyEditor;
